import React, { useState } from 'react';
import { MusicIcon, PlayIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { useDarkMode } from '../../context/DarkModeContext';
import UnifiedBookCard from '../../components/Books/UnifiedBookCard';
import AudiobookCard from '../../components/Books/AudiobookCard';
import ProductCard from '../../components/Products/ProductCard';
import { authorBooks, audiobooks as audiobooksData, merchandise as merchandiseData, playlists as playlistsData, events as eventsData } from './data';
export const CollaborationsTab = ({
  collaborations
}: {
  collaborations: typeof authorBooks.collaborations;
}) => {
  const {
    darkMode
  } = useDarkMode();
  return <div className="space-y-12" data-id="element-6432" data-mp="1v8npfijtk31u">
      {collaborations.series.map((series, idx) => <div key={idx} className={`p-6 rounded-2xl ${darkMode ? 'bg-gray-800/50' : 'bg-white border border-gray-100 shadow-sm'}`} data-id="element-6433" data-mp="1vaiaqxz05x6g" data-mpi={idx}>
          <div className="mb-6" data-id="element-6434" data-mp="jua3ij8mlxog">
            <h3 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6435" data-mp="jrfnd3wnaz0u">
              {series.name}
            </h3>
            <p className={`max-w-3xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6436" data-mp="3t5zd29qbbmdc">
              {series.description}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-id="element-6437" data-mp="6w6j3k43uw1i">
            {series.books.map(book => <div key={book.id} className="w-full" data-id="element-6438" data-mp="kuwgz1yr0zqx">
                <UnifiedBookCard book={{
            ...book,
            spiceLevel: book.heatLevel,
            seriesName: series.name,
            seriesNumber: (book as any).seriesPosition
          }} data-id="element-6439" data-mp="fwuzumkvvai3" />
              </div>)}
          </div>
        </div>)}
    </div>;
};
export const AudiobooksTab = ({
  audiobooks,
  authorName
}: {
  audiobooks: typeof audiobooksData;
  authorName: string;
}) => {
  const [playing, setPlaying] = useState<string | null>(null);
  const handlePlayPause = (title: string) => {
    if (playing === title) {
      setPlaying(null);
    } else {
      setPlaying(title);
    }
  };
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-6440" data-mp="1gmhqm201waqw">
      {audiobooks.map(book => <div key={book.id} className="w-full" data-id="element-6441" data-mp="1lfarufwb90y8">
          <AudiobookCard title={book.title} coverImage={book.coverImage} narrators={[book.narrator]} audioLength={book.duration} author={authorName} genre={book.genre} spiceLevel={book.spiceLevel} tropes={book.tropes} seriesName={book.seriesName} audioSampleUrl={book.audioSampleUrl} isPlaying={playing === book.title} onPlayPause={handlePlayPause} data-id="element-6442" data-mp="1pg331eoqtfx5" />
        </div>)}
    </div>;
};
export const MerchandiseTab = ({
  merchandise
}: {
  merchandise: typeof merchandiseData;
}) => {
  return <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-id="element-6443" data-mp="2nadsmgr8w5gz">
      {merchandise.map(item => <ProductCard key={item.id} id={item.id} name={item.title} price={item.price} image={item.image} category="Merch" rating={4.8} reviews={12} data-id="element-6444" data-mp="2egsf10qtgu95" />)}
    </div>;
};
export const PlaylistsTab = ({
  playlists
}: {
  playlists: typeof playlistsData;
}) => {
  const {
    darkMode
  } = useDarkMode();
  return <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6445" data-mp="2x7a50xk9lbqt">
      {playlists.map(playlist => <div key={playlist.id} className={`group relative overflow-hidden rounded-xl aspect-video ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-6446" data-mp="2s8fknr6nff4l">
          <img src={playlist.image} alt={playlist.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-6447" data-mp="1lpqvyok06ekc" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-6" data-id="element-6448" data-mp="34nvhqp6yymgg">
            <div className="flex items-center gap-2 text-green-400 mb-2" data-id="element-6449" data-mp="2vptb9u4gv1ls">
              <MusicIcon className="w-5 h-5" data-id="element-6450" data-mp="30o4ngbi8ho76" />
              <span className="text-sm font-medium" data-id="element-6451" data-mp="30owk7pnkhrz1">{playlist.platform}</span>
            </div>
            <h3 className="text-white font-bold text-lg mb-1" data-id="element-6452" data-mp="3of0l3p9dfdmc">
              {playlist.title}
            </h3>
            <p className="text-gray-300 text-sm" data-id="element-6453" data-mp="3ser7lgrv77l2">
              {playlist.trackCount} Tracks
            </p>
          </div>
          <a href={playlist.url} className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 backdrop-blur-[2px]" data-id="element-6454" data-mp="3ehm85zmnp6ys">
            <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform" data-id="element-6455" data-mp="27zfu4ynf6n5z">
              <PlayIcon className="w-8 h-8 text-white ml-1" data-id="element-6456" data-mp="20e3mxz7irr1n" />
            </div>
          </a>
        </div>)}
    </div>;
};
export const EventsTab = ({
  events
}: {
  events: typeof eventsData;
}) => {
  const {
    darkMode
  } = useDarkMode();
  return <div className="space-y-4 max-w-4xl" data-id="element-6457" data-mp="24h9z1nhbccg1">
      {events.map(event => <div key={event.id} className={`flex flex-col sm:flex-row sm:items-center gap-6 p-6 rounded-xl border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-6458" data-mp="1grxi21fqv036">
          <div className={`w-16 h-16 rounded-lg flex flex-col items-center justify-center flex-shrink-0 ${darkMode ? 'bg-indigo-900/30 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`} data-id="element-6459" data-mp="1qiabyk9g85lz">
            <span className="text-xs font-bold uppercase" data-id="element-6460" data-mp="y0o8xt9vydc4">
              {event.date.split(' ')[0]}
            </span>
            <span className="text-xl font-bold" data-id="element-6461" data-mp="t1l8km4uovah">
              {event.date.split(' ')[1].replace(',', '')}
            </span>
          </div>
          <div className="flex-1" data-id="element-6462" data-mp="2y3pdcoeom7jr">
            <div className={`text-sm font-medium mb-1 ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-6463" data-mp="2y2xg795fs1rd">
              {event.type}
            </div>
            <h3 className={`text-lg font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6464" data-mp="37t8u9oiu3rbz">
              {event.name}
            </h3>
            <div className={`flex flex-wrap gap-4 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6465" data-mp="2a8350vakx7n5">
              <div className="flex items-center gap-1.5" data-id="element-6466" data-mp="2k3ei796ca6ss">
                <ClockIcon className="w-4 h-4" data-id="element-6467" data-mp="2k60oz6leoi9y" />
                {event.time}
              </div>
              <div className="flex items-center gap-1.5" data-id="element-6468" data-mp="2o3ezd1ktow3j">
                <MapPinIcon className="w-4 h-4" data-id="element-6469" data-mp="3vi1mpb08zh1u" />
                {event.location}
              </div>
            </div>
          </div>
          <button className="px-6 py-2.5 rounded-lg font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors" data-id="element-6470" data-mp="3hdfls57q4ro">
            Register
          </button>
        </div>)}
    </div>;
};